import { createClient } from '@supabase/supabase-js';
import { Service, Booking, Review, ShowcaseItem, Package, AddOn } from '../types';
// Fetch all public image URLs from the 'test' storage bucket
export const getShowcaseImageUrls = async (): Promise<string[]> => {
  // Use the 'showcase' storage bucket for public images. Change the bucket name in
  // Supabase storage if you used a different bucket.
  // Default bucket is 'test' (project uses a 'test' bucket for showcase images).
  const bucketName = process.env.REACT_APP_SHOWCASE_BUCKET || 'test';
  const { data, error } = await supabase.storage.from(bucketName).list('', { limit: 100 });
  if (error) {
    console.error('[Supabase] Error listing images from bucket:', bucketName, error);
    return [];
  }
  if (!data) return [];
  // Filter for image files only
  const files = data.filter(item => item.name.match(/\.(jpg|jpeg|png|webp|gif)$/i));
  const urls: string[] = [];
  for (const file of files) {
    try {
      const publicRes = supabase.storage.from(bucketName).getPublicUrl(file.name);
      const publicUrl = publicRes?.data?.publicUrl ?? null;
      if (publicUrl) urls.push(publicUrl);
      else console.warn('[Supabase] No public URL for file', file.name, publicRes);
    } catch (err) {
      console.warn('[Supabase] Failed to get public URL for', file.name, err);
    }
  }
  return urls;
};
// AddOns
export const getAddOns = async (): Promise<AddOn[]> => {
  const { data, error } = await supabase
    .from('add_ons')
    .select('*');
  if (error) {
    console.error('[Supabase] Error fetching add_ons:', error);
    throw error;
  }
  return data || [];
};
// Showcase Items
export const getShowcaseItems = async (): Promise<ShowcaseItem[]> => {
  const { data, error } = await supabase
    .from('showcase_items')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[Supabase] Error fetching showcase_items:', error);
    throw error;
  }
  if (!data) {
    console.error('[Supabase] No data returned for showcase_items');
    return [];
  }
  return data;
};

export const addShowcaseItem = async (item: Omit<ShowcaseItem, 'id' | 'created_at'>): Promise<ShowcaseItem> => {
  const { data, error } = await supabase
    .from('showcase_items')
    .insert([item])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updateShowcaseItem = async (id: string, updates: Partial<ShowcaseItem>): Promise<ShowcaseItem> => {
  const { data, error } = await supabase
    .from('showcase_items')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deleteShowcaseItem = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('showcase_items')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

// Packages
export const getPackages = async (): Promise<Package[]> => {
  const { data, error } = await supabase
    .from('packages')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) {
    console.error('[Supabase] Error fetching packages:', error);
    throw error;
  }
  if (!data) {
    console.error('[Supabase] No data returned for packages');
    return [];
  }
  return data;
};

export const addPackage = async (pkg: Omit<Package, 'id' | 'created_at'>): Promise<Package> => {
  const { data, error } = await supabase
    .from('packages')
    .insert([pkg])
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const updatePackage = async (id: string, updates: Partial<Package>): Promise<Package> => {
  const { data, error } = await supabase
    .from('packages')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const deletePackage = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('packages')
    .delete()
    .eq('id', id);
  if (error) throw error;
};

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

// Log presence of Supabase envs (do not log keys)
console.info('[Supabase] client init - env presence', {
  hasUrl: !!supabaseUrl,
  hasAnonKey: !!supabaseAnonKey
});

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Helper: return the authenticated user's profile row from the `users` table
// Return the user's profile (id, email, role, full_name, phone) from the `users` table.
// Matches your table columns: id (uuid), email, role, full_name, phone, created_at, updated_at
export const getProfile = async (): Promise<{
  id: string;
  email: string | null;
  role: string | null;
  full_name: string | null;
  phone: string | null;
  created_at?: string;
  updated_at?: string;
} | null> => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const uid = sessionData?.session?.user?.id;
    console.debug('[Supabase] getProfile called, session user id:', uid);
    if (!uid) return null;
    const { data, error } = await supabase
      .from('users')
      .select('id, email, role, full_name, phone, created_at, updated_at')
      .eq('id', uid)
      .maybeSingle();
    if (error) {
      console.warn('[Supabase] getProfile error:', error);
      return null;
    }
    if (!data) {
      // No profile row exists for this authenticated user yet. This is expected
      // in some flows (e.g., user created via admin panel or external auth). Return
      // null and let callers handle profile creation/upsert if desired.
      return null;
    }
    return data;
  } catch (err) {
    console.error('[Supabase] getProfile unexpected error:', err);
    return null;
  }
};

// Return both the auth session (if any) and the corresponding profile row.
export const getSessionUser = async (): Promise<{
  session: any | null;
  profile: {
    id: string;
    email: string | null;
    role: string | null;
    full_name: string | null;
    phone: string | null;
    created_at?: string;
    updated_at?: string;
  } | null;
}> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData?.session ?? null;
  const profile = await getProfile();
  return { session, profile };
};

// Subscribe to auth state changes (returns unsubscribe)
export const onAuthChange = (cb: (event: string | null, session: any) => void) => {
  const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
    try { cb(event, session); } catch (e) { console.error('onAuthChange callback error', e); }
  });
  return () => sub?.subscription.unsubscribe();
};

// Services

// Fetch services with add_ons column (array of add-on IDs)
export const getServices = async (): Promise<(Service & { add_ons?: string[] })[]> => {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  const services = (data || []).map(service => ({
    ...service,
    add_ons: typeof service.add_ons === 'string' && service.add_ons.trim() !== ''
      ? service.add_ons.split(',').map((id: string) => id.trim())
      : [],
  }));
  return services;
};

export const getService = async (id: string): Promise<Service | null> => {
  const { data, error } = await supabase
    .from('services')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) throw error;
  return data;
};

// Bookings
export const createBooking = async (
  booking: Omit<Booking, 'id' | 'created_at' | 'updated_at'>,
  opts?: { includeUser?: boolean }
): Promise<any> => {
  // selector: return all booking columns. If includeUser is true, return joined user data too.
  const selector = opts?.includeUser ? '*, users(id, email, full_name, phone)' : '*';
  const { data, error } = await supabase
    .from('bookings')
    .insert([booking])
    .select(selector)
    .single();

  if (error) throw error;
  return data;
};

// Fetch bookings for current user, or all if admin
export const getBookings = async (all: boolean = false): Promise<Booking[]> => {
  // If all=true, fetch all bookings (for admin dashboard)
  if (all) {
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .order('scheduled_date', { ascending: true });
    if (error) {
      console.error('[Supabase] getBookings(all=true) error - likely RLS/permission issue:', error);
      throw error;
    }
    return data || [];
  }
  // Otherwise, fetch only bookings for current user
  const user = await getCurrentUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('user_id', user.id)
    .order('scheduled_date', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const updateBooking = async (id: string, updates: Partial<Booking>): Promise<Booking> => {
  const { data, error } = await supabase
    .from('bookings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Reviews
export const getReviews = async (): Promise<Review[]> => {
  const { data, error } = await supabase
    .from('reviews')
    .select('*')
    .order('review_date', { ascending: false })
    .limit(10);
  
  if (error) throw error;
  return data || [];
};

// Authentication
export const signInWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  
  if (error) throw error;
  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const getCurrentUser = async () => {
  const res = await supabase.auth.getUser();
  const user = res?.data?.user ?? null;
  console.debug('[Supabase] getCurrentUser:', { id: user?.id ?? null, email: user?.email ?? null });
  return user;
};